// src/pages/public/DoctorDirectory.js
import React, { useState, useEffect } from 'react';
import { Search, MapPin, Star, Filter, X } from 'lucide-react';
import apiService from '../../services/api';
import DoctorList from '../../components/lists/DoctorList';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const DoctorDirectory = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    specialization: '',
    hospital: '',
    minRating: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState('');

  const specializations = [
    'All Specializations',
    'General Medicine',
    'Cardiology',
    'Dermatology',
    'Emergency Medicine',
    'Family Medicine',
    'Internal Medicine',
    'Neurology',
    'Obstetrics and Gynecology',
    'Oncology',
    'Ophthalmology',
    'Orthopedics',
    'Pediatrics',
    'Psychiatry',
    'Radiology',
    'Surgery',
    'Urology'
  ];

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {};
      if (filters.specialization && filters.specialization !== 'All Specializations') {
        params.specialization = filters.specialization;
      }
      if (filters.hospital) {
        params.hospital = filters.hospital;
      }

      const response = await apiService.doctors.getAll(params);
      
      if (response.success) {
        let doctorsList = response.data || [];
        
        // Apply client-side filters
        if (filters.minRating > 0) {
          doctorsList = doctorsList.filter(doc => 
            (doc.rating?.average || 0) >= filters.minRating
          );
        }
        
        if (searchTerm.trim()) {
          const search = searchTerm.toLowerCase();
          doctorsList = doctorsList.filter(doc => 
            doc.firstName?.toLowerCase().includes(search) ||
            doc.lastName?.toLowerCase().includes(search) ||
            doc.specialization?.toLowerCase().includes(search) ||
            doc.hospital?.name?.toLowerCase().includes(search)
          );
        }
        
        setDoctors(doctorsList);
      } else {
        setError('Failed to load doctors');
      }
    } catch (err) {
      setError(err.message || 'Failed to load doctors');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDoctors();
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      specialization: '',
      hospital: '',
      minRating: 0
    });
    setSearchTerm('');
  };

  const hasActiveFilters = filters.specialization || filters.hospital || filters.minRating > 0 || searchTerm;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Find a Doctor</h1>
          <p className="text-lg text-primary-100 mb-6">
            Browse our directory of qualified healthcare professionals
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-3xl">
            <div className="bg-white rounded-lg shadow-lg p-2 flex items-center">
              <Search className="w-5 h-5 text-gray-400 ml-3" />
              <input
                type="text"
                placeholder="Search by doctor name, specialization, or hospital..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-3 text-gray-900 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors mr-2"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar */}
          <aside className={`lg:w-64 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                )}
              </div>

              <div className="space-y-6">
                {/* Specialization Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specialization
                  </label>
                  <select
                    value={filters.specialization}
                    onChange={(e) => handleFilterChange('specialization', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {specializations.map(spec => (
                      <option key={spec} value={spec === 'All Specializations' ? '' : spec}>
                        {spec}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Rating Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Rating
                  </label>
                  <div className="space-y-2">
                    {[4, 3, 2, 0].map(rating => (
                      <label key={rating} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="rating"
                          checked={filters.minRating === rating}
                          onChange={() => handleFilterChange('minRating', rating)}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex items-center gap-1">
                          {rating > 0 ? (
                            <>
                              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              <span className="text-sm text-gray-700">{rating}+ Stars</span>
                            </>
                          ) : (
                            <span className="text-sm text-gray-700">All Ratings</span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Apply Filters Button */}
                <button
                  onClick={fetchDoctors}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Results Header */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex items-center justify-between">
                <p className="text-gray-700">
                  {loading ? (
                    'Loading doctors...'
                  ) : (
                    <>
                      <span className="font-semibold">{doctors.length}</span> doctor
                      {doctors.length !== 1 ? 's' : ''} found
                    </>
                  )}
                </p>
                {hasActiveFilters && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Filter className="w-4 h-4" />
                    Filters active
                  </div>
                )}
              </div>
            </div>

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6">
                {error}
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : doctors.length === 0 ? (
              /* Empty State */
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No doctors found
                </h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your filters or search criteria
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              /* Doctor List */
              <DoctorList doctors={doctors} />
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default DoctorDirectory;